import { jsxs, jsx } from "react/jsx-runtime";
import find from "lodash.find";
import { useMemo, useState, useCallback, useEffect } from "react";
import { SlideAlert } from "../../../common/UI/Loaders/Activity.js";
import { FetchStatus } from "../../../utils/buffer/types.js";
import { List } from "./List.js";
import { Pills } from "./Pills.js";
import { StatusBar } from "./StatusBar.js";
import { Container, SearchBar, Input } from "./Styles.js";
import { useLookupOptions, MAX_OPTIONS } from "./useLookupOptions.js";
import { useTranslation } from "react-i18next";
const getValue = (value, singleSelect, keyPressValue) => {
  const valueIsCorrectType = Array.isArray(value);
  if (!valueIsCorrectType && value !== void 0) {
    console.warn(
      `This field's value is not an array. Value type: ${value === null ? "null" : typeof value}.`
    );
  }
  if (singleSelect && keyPressValue) {
    return [];
  }
  return valueIsCorrectType ? value ?? [] : [];
};
const Editor = ({
  data,
  keyPressValue,
  editorRef,
  onUpdateCell,
  setCurrentValue,
  refetchOnChange,
  singleSelect,
  keys,
  options,
  columnConfig,
  isFirstColumn,
  isFirstNonHeaderRow,
  isNewRowCell,
  rowData,
  ...props
}) => {
  const initOptions = options ?? (columnConfig == null ? void 0 : columnConfig.options) ?? [];
  const { t } = useTranslation();
  const {
    fetchStatus,
    filteredOptionsList,
    resetOptions,
    inputValue,
    onInputChange
  } = useLookupOptions({
    options: initOptions,
    keys,
    keyPressValue,
    refetchOnChange,
    rowData
  });
  const value = getValue(data.value, singleSelect, keyPressValue);
  const cellStatus = useMemo(() => {
    if (fetchStatus === FetchStatus.failed) {
      return { warning: true };
    }
    const error = value.some((item) => item.error);
    const warning = value.some((item) => item.warning);
    return {
      error: error || props.error,
      warning: warning || props.warning
    };
  }, [fetchStatus, props.error, props.warning, value]);
  const initSelected = singleSelect && value.length > 1 ? [value[0]] : value;
  const [selected, setSelected] = useState(
    initSelected ?? []
  );
  const [shouldFocusList, setShouldFocusList] = useState(false);
  const focusEditor = useCallback(() => {
    var _a;
    if (editorRef == null ? void 0 : editorRef.current) {
      setShouldFocusList(false);
      (_a = editorRef == null ? void 0 : editorRef.current) == null ? void 0 : _a.focus();
    }
  }, [editorRef, setShouldFocusList]);
  const focusList = useCallback(() => {
    setShouldFocusList(true);
  }, [setShouldFocusList]);
  const selectItem = (item) => {
    if (!find(selected, item) || singleSelect) {
      const selections = singleSelect ? [item] : [...selected, item];
      setCurrentValue(selections);
      setSelected(selections);
      resetOptions();
      if (singleSelect) {
        onUpdateCell(item.value ?? item.label, true);
      }
    }
  };
  const removeItem = useCallback(
    (item) => {
      const selectedWithoutItem = selected.filter(
        (i) => i.label !== item.label || i.value !== item.value
      );
      setCurrentValue(selectedWithoutItem);
      setSelected(selectedWithoutItem);
      resetOptions();
    },
    [resetOptions, selected, setCurrentValue]
  );
  const handleKeydown = (event) => {
    if (event.key === "Backspace" && selected.length && inputValue === "") {
      removeItem(selected[selected.length - 1]);
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusList();
    }
    if (isNewRowCell && event.key === "ArrowUp") {
      event.preventDefault();
      focusList();
    }
  };
  useEffect(() => {
    if (fetchStatus === FetchStatus.loading) {
      return;
    } else if (filteredOptionsList.length !== 1) {
      focusEditor();
    } else if (filteredOptionsList.length) {
      focusList();
    }
  }, [fetchStatus]);
  useEffect(() => {
    if (filteredOptionsList.length === 1) {
      focusList();
    }
  }, [filteredOptionsList]);
  useEffect(() => {
    focusEditor();
  }, [editorRef, selected]);
  const hasMore = filteredOptionsList.length >= MAX_OPTIONS;
  return /* @__PURE__ */ jsxs(
    Container,
    {
      ...cellStatus,
      className: "lookup-editor",
      isNewRowCell,
      children: [
        /* @__PURE__ */ jsxs(
          SearchBar,
          {
            isFirstColumn,
            isFirstNonHeaderRow,
            isNewRowCell,
            children: [
              (selected == null ? void 0 : selected.length) > 0 && /* @__PURE__ */ jsx(Pills, { options: selected, removeItem, editor: true }),
              /* @__PURE__ */ jsx(
                Input,
                {
                  "aria-label": "item-filter",
                  type: "text",
                  role: "input",
                  placeholder: t("sheet.table.cells.enum.placeholder"),
                  onChange: onInputChange,
                  onKeyDown: handleKeydown,
                  value: inputValue,
                  ref: editorRef
                }
              ),
              /* @__PURE__ */ jsx(SlideAlert, { show: fetchStatus === FetchStatus.failed })
            ]
          }
        ),
        filteredOptionsList.length === 0 || fetchStatus !== FetchStatus.success ? /* @__PURE__ */ jsx(StatusBar, { fetchStatus, empty: true }) : /* @__PURE__ */ jsx(
          List,
          {
            hasMore,
            fetchStatus,
            focused: shouldFocusList,
            selectItem,
            options: filteredOptionsList,
            focusEditor,
            singleSelect,
            isNewRowCell
          }
        )
      ]
    }
  );
};
export {
  Editor as default
};
